import React, { useEffect, useState } from 'react'
import axios from 'axios'
import KitchenConfigurationGrid from '../locations/KitchenConfigurationGrid'
import { Slot, SlotType, newlyDisabledCookingUnitSlotNumbers } from '../locations/kitchenConfigurationHelper'
import DisableCookingUnitConfirmModal from '../locations/DisableCookingUnitConfirmModal'
import { t } from '../../i18n'
import Flash, { FlashMessage } from './Flash'
import SlotAlertsModal, { SlotAlertsContext } from './SlotAlertsModal'

const scope = 'controls'

type PartType =
  | 'cooking_unit'
  | 'plate_stand'
  | 'robot_tray'
  | 'locker_slot'
  | 'fixed_container_slot'
  | 'mobile_container_slot'

const SLOT_TYPE_TO_PART_TYPE: Partial<Record<SlotType, PartType>> = {
  [SlotType.COOKING_UNIT]: 'cooking_unit',
  [SlotType.PLATE_STAND]: 'plate_stand',
  [SlotType.ROBOT_TRAY]: 'robot_tray',
  [SlotType.LOCKER]: 'locker_slot',
  [SlotType.FIXED_CONTAINER]: 'fixed_container_slot',
  [SlotType.MOBILE_CONTAINER]: 'mobile_container_slot',
}

interface KitchenConfigShape {
  fixed_container_slots: Slot[]
  mobile_container_slots: Slot[]
  cooking_units: Slot[]
  plate_stands: Slot[]
  locker_slots: Slot[]
  robot_trays: Slot[]
}

const EMPTY_CONFIG: KitchenConfigShape = {
  fixed_container_slots: [],
  mobile_container_slots: [],
  cooking_units: [],
  plate_stands: [],
  locker_slots: [],
  robot_trays: [],
}

const EMPTY_ERRORS = {
  fixed_container_slots: false,
  mobile_container_slots: false,
  plate_stands: false,
  cooking_units: false,
  locker_slots: false,
  robot_trays: false,
}

interface Props {
  locationId: number
  kitchenConfigurationOptions: Record<string, { rows: number, columns: number }>
  onDirtyChange?: (dirty: boolean) => void
}

const KitchenConfigSection = ({ locationId, kitchenConfigurationOptions, onDirtyChange }: Props) => {
  const [config, setConfig] = useState<KitchenConfigShape>(EMPTY_CONFIG)
  const [savedConfig, setSavedConfig] = useState<KitchenConfigShape>(EMPTY_CONFIG)
  const [errors, setErrors] = useState(EMPTY_ERRORS)
  const [loaded, setLoaded] = useState(false)
  const [saving, setSaving] = useState(false)
  const [flash, setFlash] = useState<FlashMessage | null>(null)
  const [alertsContext, setAlertsContext] = useState<SlotAlertsContext | null>(null)
  const [disableConfirm, setDisableConfirm] = useState<number[] | null>(null)

  const isDirty = loaded && JSON.stringify(config) !== JSON.stringify(savedConfig)

  const openSlotAlerts = (slotType: SlotType) => (slot: Slot) => {
    const partType = SLOT_TYPE_TO_PART_TYPE[slotType]
    if (!partType || !slot?.id) return
    setAlertsContext({
      partType,
      partId: Number(slot.id),
      slotLabel: String(slot.slot_number ?? slot.tray_number ?? slot.id),
    })
  }

  useEffect(() => {
    axios
      .get('/api/internal/locations/current_kitchen_configuration')
      .then(({ data }) => {
        const loadedConfig = {
          fixed_container_slots: data.fixed_container_slots || [],
          mobile_container_slots: data.mobile_container_slots || [],
          cooking_units: data.cooking_units || [],
          plate_stands: data.plate_stands || [],
          locker_slots: data.locker_slots || [],
          robot_trays: data.robot_trays || [],
        }
        setConfig(loadedConfig)
        setSavedConfig(loadedConfig)
      })
      .catch(() => {
        setFlash({ type: 'danger', text: t('kitchen_config_load_failed', { scope }) })
      })
      .finally(() => setLoaded(true))
  }, [])

  // Report unsaved changes upward so the tab switch can be guarded, and warn on full-page navigation.
  useEffect(() => {
    onDirtyChange?.(isDirty)

    if (!isDirty) return

    const warnBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', warnBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', warnBeforeUnload)
    }
  }, [isDirty, onDirtyChange])

  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange])

  const hasErrors = Object.values(errors).some(Boolean)

  const formatValidationErrors = (fullErrors: Record<string, string[]>): string =>
    Object.values(fullErrors).flat().join(', ')

  const save = () => {
    setSaving(true)
    setFlash(null)

    const newlyDisabled = newlyDisabledCookingUnitSlotNumbers(config.cooking_units, savedConfig.cooking_units)
    if (newlyDisabled.length === 0) {
      performSave()
      return
    }

    axios
      .get(`/api/internal/locations/${locationId}/taken_cooking_units`, { params: { slot_numbers: newlyDisabled } })
      .then(({ data }) => {
        const takenUnits: number[] = data.taken_cooking_units || []
        if (takenUnits.length > 0) {
          setSaving(false)
          setDisableConfirm(takenUnits)
        } else {
          performSave()
        }
      })
      // The confirm is informational only — the backend handles an in-use disable safely.
      .catch(() => performSave())
  }

  const performSave = () => {
    setSaving(true)

    const formData = new FormData()
    formData.append('kitchen_configuration', JSON.stringify(config))

    axios
      .patch(`/api/internal/locations/${locationId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(() => {
        setSavedConfig(config)
        setFlash({ type: 'success', text: t('kitchen_config_saved', { scope }) })
      })
      .catch(err => {
        const fullErrors = err?.response?.data?.full_errors
        setFlash({
          type: 'danger',
          text: fullErrors ? formatValidationErrors(fullErrors) : t('kitchen_config_save_failed', { scope }),
        })
      })
      .finally(() => setSaving(false))
  }

  const setSlotFields = (slotType: SlotType) =>
    (activeFields: Slot[] | ((prev: Slot[]) => Slot[])) => {
      setConfig(prev => ({
        ...prev,
        [slotType]: typeof activeFields === 'function'
          ? (activeFields as (prev: Slot[]) => Slot[])(prev[slotType])
          : activeFields,
      }))
    }

  const renderSection = (slotType: SlotType) => {
    const fridgeColumns = slotType === SlotType.FIXED_CONTAINER ? 5 : 0
    return (
      <>
        <h5 className="mb-3">{t(`kitchen_config_sections.${slotType}`, { scope })}</h5>
        <KitchenConfigurationGrid
          slotType={slotType}
          disableAccordion={true}
          activeFields={config[slotType]}
          kitchenConfigurationOptions={kitchenConfigurationOptions}
          kitchenConfigurationErrors={errors}
          setKitchenConfigurationErrors={setErrors}
          fridgeColumns={fridgeColumns}
          setActiveFields={setSlotFields(slotType)}
          onSlotAlertsClick={openSlotAlerts(slotType)}
          hideTravelTimeInput
        />
      </>
    )
  }

  if (!loaded) return null

  const saveButton = (
    <button
      type="button"
      className="btn btn-primary btn-sm"
      onClick={save}
      disabled={saving || hasErrors}
    >
      {t('kitchen_config_save', { scope })}
      {saving && <span className="spinner-border spinner-border-sm ms-2" />}
    </button>
  )

  return (
    <div
      className="kitchen-configuration mt-5 pt-5 position-relative"
      style={{ borderTop: '3px solid #dee2e6' }}
    >
      {/* Desktop: absolute top-right, aligned with the Fixed feeders heading */}
      <div className="d-none d-md-block position-absolute" style={{ top: '3rem', right: 0 }}>
        {saveButton}
      </div>

      {/* Mobile: normal flow above the first section */}
      <div className="d-flex justify-content-end mb-3 d-md-none">
        {saveButton}
      </div>

      <Flash flash={flash} onDismiss={() => setFlash(null)} />

      <div className="mb-4">
        {renderSection(SlotType.FIXED_CONTAINER)}
      </div>

      <hr className="my-4" />

      <div className="mb-4">
        {renderSection(SlotType.MOBILE_CONTAINER)}
      </div>

      <hr className="my-4" />

      <div className="row mb-4">
        <div className="col-md-6 controls-kitchen-divider-end pe-md-4 mb-4 mb-md-0">{renderSection(SlotType.COOKING_UNIT)}</div>
        <div className="col-md-6 ps-md-4">{renderSection(SlotType.PLATE_STAND)}</div>
      </div>

      <hr className="my-4" />

      <div className="row mb-4">
        <div className="col-md-6 controls-kitchen-divider-end pe-md-4 mb-4 mb-md-0">{renderSection(SlotType.LOCKER)}</div>
        <div className="col-md-6 ps-md-4">{renderSection(SlotType.ROBOT_TRAY)}</div>
      </div>

      <SlotAlertsModal context={alertsContext} onClose={() => setAlertsContext(null)} />

      {disableConfirm && (
        <DisableCookingUnitConfirmModal
          slotNumbers={disableConfirm}
          onConfirm={() => {
            setDisableConfirm(null)
            performSave()
          }}
          onCancel={() => setDisableConfirm(null)}
        />
      )}
    </div>
  )
}

export default KitchenConfigSection
